import React, { useState, useCallback, useRef } from 'react';
import { Topic } from '../../types/Topic';
import { apiClient } from '../../services/apiClient';

interface DocumentUploaderProps {
  onTopicsExtracted: (topics: Topic[]) => void;
  onError: (error: string) => void;
}

interface UploadState {
  isDragOver: boolean;
  isProcessing: boolean;
  uploadedFile: File | null;
  extractedText: string;
}

const SUPPORTED_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({ onTopicsExtracted, onError }) => {
  const [state, setState] = useState<UploadState>({
    isDragOver: false,
    isProcessing: false,
    uploadedFile: null,
    extractedText: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) return 'File size must be under 10MB. Please try a smaller file.';
    if (!SUPPORTED_TYPES.includes(file.type)) return 'Unsupported file type. Please upload a PDF, DOCX, TXT, or MD file.';
    return null;
  };

  const getFileType = (mimeType: string): string => {
    const map: Record<string, string> = {
      'application/pdf': 'pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'text/plain': 'txt',
      'text/markdown': 'md',
    };
    return map[mimeType] || 'txt';
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
    });

  const processFile = async (file: File) => {
    setState(prev => ({ ...prev, isProcessing: true, uploadedFile: file }));
    try {
      const fileContent = await fileToBase64(file);
      const result = await apiClient.processDocument(fileContent, file.name, getFileType(file.type));
      setState(prev => ({ ...prev, isProcessing: false, extractedText: result.extractedText }));
      if (result.topics.length === 0) {
        onError('No curriculum topics could be identified in this document. Try a different file or add topics manually.');
      } else {
        onTopicsExtracted(result.topics);
      }
    } catch (err) {
      setState(prev => ({ ...prev, isProcessing: false }));
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('401') || message.includes('Authentication') || message.includes('Unauthorised')) {
        onError('You need to sign in before uploading documents.');
      } else if (message.includes('413') || message.includes('too large')) {
        onError('File is too large to process. Please try a smaller document.');
      } else if (message.includes('400')) {
        onError(`The document couldn't be processed: ${message}`);
      } else {
        onError(`Failed to process "${file.name}": ${message}`);
      }
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setState(prev => ({ ...prev, isDragOver: true })); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setState(prev => ({ ...prev, isDragOver: false })); }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setState(prev => ({ ...prev, isDragOver: false }));
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { onError(err); return; }
    processFile(file);
  }, [onError]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { onError(err); return; }
    processFile(file);
  }, [onError]);

  return (
    <div className="document-uploader">
      <div
        className={`upload-zone ${state.isDragOver ? 'drag-over' : ''} ${state.isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={handleFileSelect} style={{ display: 'none' }} />
        {state.isProcessing ? (
          <div className="processing-state">
            <div className="spinner"></div>
            <p>Processing {state.uploadedFile?.name}…</p>
          </div>
        ) : (
          <div className="upload-prompt">
            <div className="upload-icon">📄</div>
            <h3>Upload Curriculum Document</h3>
            <p>Drag and drop your document here, or click to browse</p>
            <p className="file-info">Supports PDF, DOCX, TXT, and MD files up to 10MB</p>
          </div>
        )}
      </div>
      {state.uploadedFile && !state.isProcessing && (
        <div className="file-preview">
          <h4>Uploaded File</h4>
          <div className="file-details">
            <span className="file-name">{state.uploadedFile.name}</span>
            <span className="file-size">{(state.uploadedFile.size / 1024 / 1024).toFixed(2)} MB</span>
          </div>
          {state.extractedText && (
            <div className="text-preview">
              <h5>Extracted Text Preview</h5>
              <p>{state.extractedText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
